import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type ToggleGroupType = 'single' | 'multiple';
export type ToggleGroupVariant = 'default' | 'outline' | 'solid';
export type ToggleGroupSize = 'sm' | 'md' | 'lg';

export interface ToggleGroupContextValue {
  type: ToggleGroupType;
  value: string | string[];
  onValueChange: (value: string | string[]) => void;
  variant: ToggleGroupVariant;
  size: ToggleGroupSize;
  disabled?: boolean;
}

export interface ToggleGroupProps {
  /** Selection type - single or multiple */
  type?: ToggleGroupType;
  /** Controlled value(s) */
  value?: string | string[];
  /** Default value(s) for uncontrolled mode */
  defaultValue?: string | string[];
  /** Callback when value changes */
  onValueChange?: (value: string | string[]) => void;
  /** Visual variant */
  variant?: ToggleGroupVariant;
  /** Size of toggle buttons */
  size?: ToggleGroupSize;
  /** Disable all toggle buttons */
  disabled?: boolean;
  /** Orientation of the group */
  orientation?: 'horizontal' | 'vertical';
  /** Whether buttons are attached */
  attached?: boolean;
  /** Children toggle items */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export interface ToggleGroupItemProps {
  /** Value identifier for this item */
  value: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Label text */
  children?: React.ReactNode;
  /** Disable this item */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** ARIA label for icon-only buttons */
  'aria-label'?: string;
}

export interface ToggleProps {
  /** Whether the toggle is pressed */
  pressed?: boolean;
  /** Default pressed state for uncontrolled mode */
  defaultPressed?: boolean;
  /** Callback when pressed state changes */
  onPressedChange?: (pressed: boolean) => void;
  /** Visual variant */
  variant?: ToggleGroupVariant;
  /** Size */
  size?: ToggleGroupSize;
  /** Disable the toggle */
  disabled?: boolean;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Label text */
  children?: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export interface ToolbarProps {
  /** Children elements */
  children: React.ReactNode;
  /** Orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export interface ToolbarGroupProps {
  /** Children elements */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export interface ToolbarSeparatorProps {
  /** Orientation (derived from parent) */
  orientation?: 'horizontal' | 'vertical';
  /** Additional class names */
  className?: string;
}

export interface ToolbarButtonProps {
  /** Button content */
  children: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Disable button */
  disabled?: boolean;
  /** Button variant */
  variant?: 'default' | 'ghost';
  /** Button size */
  size?: ToggleGroupSize;
  /** Whether button is active */
  active?: boolean;
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export interface UseToggleGroupOptions {
  /** Selection type */
  type?: ToggleGroupType;
  /** Default value(s) */
  defaultValue?: string | string[];
  /** Callback when value changes */
  onValueChange?: (value: string | string[]) => void;
  /** Allow deselecting all in single mode */
  allowDeselect?: boolean;
}

export interface UseToggleGroupReturn {
  /** Current value(s) */
  value: string | string[];
  /** Handle value change */
  handleValueChange: (itemValue: string) => void;
  /** Check if an item is selected */
  isSelected: (itemValue: string) => boolean;
  /** Get props for an item */
  getItemProps: (itemValue: string) => {
    'aria-pressed': boolean;
    onClick: () => void;
  };
}

// ============================================================================
// Context
// ============================================================================

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

function useToggleGroupContext(): ToggleGroupContextValue {
  const context = React.useContext(ToggleGroupContext);
  if (!context) {
    throw new Error('ToggleGroupItem must be used within a ToggleGroup');
  }
  return context;
}

// ============================================================================
// Style Utilities
// ============================================================================

const sizeStyles: Record<ToggleGroupSize, string> = {
  sm: 'h-8 px-2.5 text-sm gap-1.5',
  md: 'h-9 px-3 text-sm gap-2',
  lg: 'h-10 px-4 text-base gap-2',
};

const iconSizeStyles: Record<ToggleGroupSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const iconElementSizes: Record<ToggleGroupSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

function getVariantStyles(variant: ToggleGroupVariant, pressed: boolean): string {
  switch (variant) {
    case 'outline':
      return pressed
        ? 'border border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';
    case 'solid':
      return pressed
        ? 'bg-blue-600 text-white dark:bg-blue-500'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600';
    default:
      return pressed
        ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white'
        : 'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800';
  }
}

// ============================================================================
// Hook: useToggleGroup
// ============================================================================

export function useToggleGroup(options: UseToggleGroupOptions = {}): UseToggleGroupReturn {
  const {
    type = 'single',
    defaultValue,
    onValueChange,
    allowDeselect = true,
  } = options;

  const [value, setValue] = React.useState<string | string[]>(() => {
    if (defaultValue !== undefined) return defaultValue;
    return type === 'single' ? '' : [];
  });

  const handleValueChange = React.useCallback((itemValue: string) => {
    setValue((current) => {
      let newValue: string | string[];

      if (type === 'single') {
        // Single selection
        const currentStr = current as string;
        if (currentStr === itemValue && allowDeselect) {
          newValue = '';
        } else {
          newValue = itemValue;
        }
      } else {
        // Multiple selection
        const currentArr = current as string[];
        if (currentArr.includes(itemValue)) {
          newValue = currentArr.filter((v) => v !== itemValue);
        } else {
          newValue = [...currentArr, itemValue];
        }
      }

      onValueChange?.(newValue);
      return newValue;
    });
  }, [type, allowDeselect, onValueChange]);

  const isSelected = React.useCallback((itemValue: string) => {
    if (type === 'single') {
      return value === itemValue;
    }
    return (value as string[]).includes(itemValue);
  }, [type, value]);

  const getItemProps = React.useCallback((itemValue: string) => ({
    'aria-pressed': isSelected(itemValue),
    onClick: () => handleValueChange(itemValue),
  }), [isSelected, handleValueChange]);

  return {
    value,
    handleValueChange,
    isSelected,
    getItemProps,
  };
}

// ============================================================================
// ToggleGroup Component
// ============================================================================

export function ToggleGroup({
  type = 'single',
  value: controlledValue,
  defaultValue,
  onValueChange,
  variant = 'default',
  size = 'md',
  disabled = false,
  orientation = 'horizontal',
  attached = true,
  children,
  className,
  'aria-label': ariaLabel,
}: ToggleGroupProps): React.ReactElement {
  const [internalValue, setInternalValue] = React.useState<string | string[]>(() => {
    if (defaultValue !== undefined) return defaultValue;
    return type === 'single' ? '' : [];
  });

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = React.useCallback((newValue: string | string[]) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  }, [isControlled, onValueChange]);

  const contextValue = React.useMemo(() => ({
    type,
    value,
    onValueChange: handleValueChange,
    variant,
    size,
    disabled,
  }), [type, value, handleValueChange, variant, size, disabled]);

  return (
    <ToggleGroupContext.Provider value={contextValue}>
      <div
        role="group"
        aria-label={ariaLabel}
        data-orientation={orientation}
        className={clsx(
          'inline-flex',
          orientation === 'vertical' ? 'flex-col' : 'flex-row',
          attached && orientation === 'horizontal' && '[&>button:not(:first-child)]:rounded-l-none [&>button:not(:last-child)]:rounded-r-none [&>button:not(:first-child)]:-ml-px',
          attached && orientation === 'vertical' && '[&>button:not(:first-child)]:rounded-t-none [&>button:not(:last-child)]:rounded-b-none [&>button:not(:first-child)]:-mt-px',
          !attached && 'gap-1',
          className
        )}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

// ============================================================================
// ToggleGroupItem Component
// ============================================================================

export function ToggleGroupItem({
  value: itemValue,
  icon,
  children,
  disabled: itemDisabled,
  className,
  'aria-label': ariaLabel,
}: ToggleGroupItemProps): React.ReactElement {
  const { type, value, onValueChange, variant, size, disabled: groupDisabled } = useToggleGroupContext();

  const isDisabled = itemDisabled || groupDisabled;
  const isPressed = type === 'single'
    ? value === itemValue
    : (value as string[]).includes(itemValue);

  const handleClick = () => {
    if (isDisabled) return;

    if (type === 'single') {
      // In single mode, clicking same item deselects it
      const newValue = value === itemValue ? '' : itemValue;
      onValueChange(newValue);
    } else {
      // In multiple mode, toggle the item
      const currentArr = value as string[];
      const newValue = currentArr.includes(itemValue)
        ? currentArr.filter((v) => v !== itemValue)
        : [...currentArr, itemValue];
      onValueChange(newValue);
    }
  };

  const isIconOnly = icon && !children;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isPressed}
      aria-label={ariaLabel}
      disabled={isDisabled}
      onClick={handleClick}
      className={clsx(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        isIconOnly ? iconSizeStyles[size] : sizeStyles[size],
        getVariantStyles(variant, isPressed),
        className
      )}
    >
      {icon && (
        <span className={iconElementSizes[size]}>
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

// ============================================================================
// Toggle Component (Standalone)
// ============================================================================

export function Toggle({
  pressed: controlledPressed,
  defaultPressed = false,
  onPressedChange,
  variant = 'default',
  size = 'md',
  disabled = false,
  icon,
  children,
  className,
  'aria-label': ariaLabel,
}: ToggleProps): React.ReactElement {
  const [internalPressed, setInternalPressed] = React.useState(defaultPressed);
  const isControlled = controlledPressed !== undefined;
  const pressed = isControlled ? controlledPressed : internalPressed;

  const handleClick = () => {
    if (disabled) return;
    const newPressed = !pressed;
    if (!isControlled) {
      setInternalPressed(newPressed);
    }
    onPressedChange?.(newPressed);
  };

  const isIconOnly = icon && !children;

  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={handleClick}
      className={clsx(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        isIconOnly ? iconSizeStyles[size] : sizeStyles[size],
        getVariantStyles(variant, pressed),
        className
      )}
    >
      {icon && (
        <span className={iconElementSizes[size]}>
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

// ============================================================================
// Toolbar Components
// ============================================================================

const ToolbarContext = React.createContext<{ orientation: 'horizontal' | 'vertical' }>({
  orientation: 'horizontal',
});

export function Toolbar({
  children,
  orientation = 'horizontal',
  className,
  'aria-label': ariaLabel,
}: ToolbarProps): React.ReactElement {
  return (
    <ToolbarContext.Provider value={{ orientation }}>
      <div
        role="toolbar"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        className={clsx(
          'flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800',
          orientation === 'vertical' && 'flex-col',
          className
        )}
      >
        {children}
      </div>
    </ToolbarContext.Provider>
  );
}

export function ToolbarGroup({
  children,
  className,
}: ToolbarGroupProps): React.ReactElement {
  return (
    <div className={clsx('flex items-center gap-0.5', className)}>
      {children}
    </div>
  );
}

export function ToolbarSeparator({
  orientation: explicitOrientation,
  className,
}: ToolbarSeparatorProps): React.ReactElement {
  const { orientation: contextOrientation } = React.useContext(ToolbarContext);
  const orientation = explicitOrientation ?? contextOrientation;

  return (
    <div
      role="separator"
      aria-orientation={orientation === 'horizontal' ? 'vertical' : 'horizontal'}
      className={clsx(
        'bg-gray-300 dark:bg-gray-600',
        orientation === 'horizontal' ? 'w-px h-6 mx-1' : 'h-px w-6 my-1',
        className
      )}
    />
  );
}

export function ToolbarButton({
  children,
  onClick,
  icon,
  disabled = false,
  variant = 'ghost',
  size = 'sm',
  active = false,
  className,
  'aria-label': ariaLabel,
}: ToolbarButtonProps): React.ReactElement {
  const isIconOnly = icon && !children;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        'disabled:pointer-events-none disabled:opacity-50',
        isIconOnly ? iconSizeStyles[size] : sizeStyles[size],
        variant === 'ghost'
          ? active
            ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
            : 'text-gray-700 hover:bg-white/50 dark:text-gray-300 dark:hover:bg-gray-700/50'
          : active
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
        className
      )}
    >
      {icon && (
        <span className={iconElementSizes[size]}>
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

// ============================================================================
// Compound ToggleGroup with Labels
// ============================================================================

export interface ToggleOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SimpleToggleGroupProps {
  /** Available options */
  options: ToggleOption[];
  /** Current value(s) */
  value?: string | string[];
  /** Default value(s) */
  defaultValue?: string | string[];
  /** Selection type */
  type?: ToggleGroupType;
  /** Callback when value changes */
  onValueChange?: (value: string | string[]) => void;
  /** Visual variant */
  variant?: ToggleGroupVariant;
  /** Size */
  size?: ToggleGroupSize;
  /** Disable all options */
  disabled?: boolean;
  /** Show icons only (labels as aria-labels) */
  iconsOnly?: boolean;
  /** Additional class names */
  className?: string;
  /** ARIA label for the group */
  'aria-label'?: string;
}

export function SimpleToggleGroup({
  options,
  value,
  defaultValue,
  type = 'single',
  onValueChange,
  variant = 'default',
  size = 'md',
  disabled = false,
  iconsOnly = false,
  className,
  'aria-label': ariaLabel,
}: SimpleToggleGroupProps): React.ReactElement {
  return (
    <ToggleGroup
      type={type}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      variant={variant}
      size={size}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          icon={option.icon}
          disabled={option.disabled}
          aria-label={iconsOnly ? option.label : undefined}
        >
          {!iconsOnly && option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

// ============================================================================
// ViewModeToggle - Common Pattern
// ============================================================================

export interface ViewModeToggleProps {
  /** Current view mode */
  value?: string;
  /** Default view mode */
  defaultValue?: string;
  /** Callback when view mode changes */
  onValueChange?: (value: string) => void;
  /** Available view modes */
  modes?: Array<{
    value: string;
    label: string;
    icon: React.ReactNode;
  }>;
  /** Size */
  size?: ToggleGroupSize;
  /** Additional class names */
  className?: string;
}

const defaultViewModes = [
  { value: 'grid', label: 'Grid view', icon: '▦' },
  { value: 'list', label: 'List view', icon: '☰' },
];

export function ViewModeToggle({
  value,
  defaultValue = 'grid',
  onValueChange,
  modes = defaultViewModes,
  size = 'sm',
  className,
}: ViewModeToggleProps): React.ReactElement {
  const handleChange = (newValue: string | string[]) => {
    // Only pass non-empty values for view mode
    if (typeof newValue === 'string' && newValue) {
      onValueChange?.(newValue);
    }
  };

  return (
    <ToggleGroup
      type="single"
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleChange}
      variant="outline"
      size={size}
      className={className}
      aria-label="View mode"
    >
      {modes.map((mode) => (
        <ToggleGroupItem
          key={mode.value}
          value={mode.value}
          icon={mode.icon}
          aria-label={mode.label}
        />
      ))}
    </ToggleGroup>
  );
}

// ============================================================================
// AlignmentToggle - Common Pattern
// ============================================================================

export interface AlignmentToggleProps {
  /** Current alignment */
  value?: string;
  /** Default alignment */
  defaultValue?: string;
  /** Callback when alignment changes */
  onValueChange?: (value: string) => void;
  /** Orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Size */
  size?: ToggleGroupSize;
  /** Additional class names */
  className?: string;
}

export function AlignmentToggle({
  value,
  defaultValue = 'left',
  onValueChange,
  orientation = 'horizontal',
  size = 'sm',
  className,
}: AlignmentToggleProps): React.ReactElement {
  const alignments = orientation === 'horizontal'
    ? [
        { value: 'left', label: 'Align left', icon: '⬛◻◻' },
        { value: 'center', label: 'Align center', icon: '◻⬛◻' },
        { value: 'right', label: 'Align right', icon: '◻◻⬛' },
      ]
    : [
        { value: 'top', label: 'Align top', icon: '⬆' },
        { value: 'middle', label: 'Align middle', icon: '⬌' },
        { value: 'bottom', label: 'Align bottom', icon: '⬇' },
      ];

  const handleChange = (newValue: string | string[]) => {
    if (typeof newValue === 'string' && newValue) {
      onValueChange?.(newValue);
    }
  };

  return (
    <ToggleGroup
      type="single"
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleChange}
      variant="default"
      size={size}
      className={className}
      aria-label={orientation === 'horizontal' ? 'Text alignment' : 'Vertical alignment'}
    >
      {alignments.map((alignment) => (
        <ToggleGroupItem
          key={alignment.value}
          value={alignment.value}
          icon={alignment.icon}
          aria-label={alignment.label}
        />
      ))}
    </ToggleGroup>
  );
}

// ============================================================================
// FontStyleToggle - Common Pattern for Text Formatting
// ============================================================================

export interface FontStyleToggleProps {
  /** Currently active styles */
  value?: string[];
  /** Default active styles */
  defaultValue?: string[];
  /** Callback when styles change */
  onValueChange?: (value: string[]) => void;
  /** Size */
  size?: ToggleGroupSize;
  /** Additional class names */
  className?: string;
}

export function FontStyleToggle({
  value,
  defaultValue = [],
  onValueChange,
  size = 'sm',
  className,
}: FontStyleToggleProps): React.ReactElement {
  const handleChange = (newValue: string | string[]) => {
    onValueChange?.(newValue as string[]);
  };

  return (
    <ToggleGroup
      type="multiple"
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleChange}
      variant="default"
      size={size}
      className={className}
      aria-label="Text formatting"
    >
      <ToggleGroupItem value="bold" aria-label="Bold">
        <span className="font-bold">B</span>
      </ToggleGroupItem>
      <ToggleGroupItem value="italic" aria-label="Italic">
        <span className="italic">I</span>
      </ToggleGroupItem>
      <ToggleGroupItem value="underline" aria-label="Underline">
        <span className="underline">U</span>
      </ToggleGroupItem>
      <ToggleGroupItem value="strikethrough" aria-label="Strikethrough">
        <span className="line-through">S</span>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

// ============================================================================
// Segmented Control - iOS-style Tab Toggle
// ============================================================================

export interface SegmentedControlProps {
  /** Available segments */
  segments: Array<{
    value: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
  }>;
  /** Current value */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** Size */
  size?: ToggleGroupSize;
  /** Full width */
  fullWidth?: boolean;
  /** Additional class names */
  className?: string;
}

export function SegmentedControl({
  segments,
  value: controlledValue,
  defaultValue,
  onValueChange,
  size = 'md',
  fullWidth = false,
  className,
}: SegmentedControlProps): React.ReactElement {
  const [internalValue, setInternalValue] = React.useState(
    defaultValue ?? segments[0]?.value ?? ''
  );

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleSelect = (segmentValue: string) => {
    if (!isControlled) {
      setInternalValue(segmentValue);
    }
    onValueChange?.(segmentValue);
  };

  const segmentSizeStyles: Record<ToggleGroupSize, string> = {
    sm: 'h-7 px-3 text-xs',
    md: 'h-8 px-4 text-sm',
    lg: 'h-10 px-5 text-base',
  };

  return (
    <div
      role="tablist"
      className={clsx(
        'inline-flex p-1 rounded-lg bg-gray-100 dark:bg-gray-800',
        fullWidth && 'w-full',
        className
      )}
    >
      {segments.map((segment) => (
        <button
          key={segment.value}
          role="tab"
          type="button"
          aria-selected={value === segment.value}
          disabled={segment.disabled}
          onClick={() => handleSelect(segment.value)}
          className={clsx(
            'inline-flex items-center justify-center gap-2 rounded-md font-medium',
            'transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
            'disabled:pointer-events-none disabled:opacity-50',
            segmentSizeStyles[size],
            fullWidth && 'flex-1',
            value === segment.value
              ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
          )}
        >
          {segment.icon && (
            <span className={iconElementSizes[size]}>{segment.icon}</span>
          )}
          {segment.label}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// ChipGroup - Pill-style Multi-Select
// ============================================================================

export interface ChipGroupProps {
  /** Available chips */
  chips: Array<{
    value: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
  }>;
  /** Current selected values */
  value?: string[];
  /** Default selected values */
  defaultValue?: string[];
  /** Selection type */
  type?: 'single' | 'multiple';
  /** Callback when values change */
  onValueChange?: (value: string[]) => void;
  /** Size */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export function ChipGroup({
  chips,
  value: controlledValue,
  defaultValue = [],
  type = 'multiple',
  onValueChange,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: ChipGroupProps): React.ReactElement {
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleToggle = (chipValue: string) => {
    let newValue: string[];

    if (type === 'single') {
      newValue = value.includes(chipValue) ? [] : [chipValue];
    } else {
      newValue = value.includes(chipValue)
        ? value.filter((v) => v !== chipValue)
        : [...value, chipValue];
    }

    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const chipSizeStyles: Record<string, string> = {
    sm: 'h-6 px-2 text-xs gap-1',
    md: 'h-8 px-3 text-sm gap-1.5',
    lg: 'h-10 px-4 text-base gap-2',
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={clsx('flex flex-wrap gap-2', className)}
    >
      {chips.map((chip) => {
        const isSelected = value.includes(chip.value);

        return (
          <button
            key={chip.value}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            disabled={chip.disabled}
            onClick={() => handleToggle(chip.value)}
            className={clsx(
              'inline-flex items-center justify-center rounded-full font-medium',
              'transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
              'disabled:pointer-events-none disabled:opacity-50',
              chipSizeStyles[size],
              isSelected
                ? 'bg-blue-100 text-blue-800 border-2 border-blue-500 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            )}
          >
            {chip.icon && (
              <span className={iconElementSizes[size]}>{chip.icon}</span>
            )}
            {chip.label}
            {isSelected && (
              <span className="ml-1 text-blue-600 dark:text-blue-400">✓</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
